// Accounts receivable: ONE open-items engine for every AR report.
//
// Before 2026-10-06 the AR Aging Summary, the AR Aging Detail and the
// Customer Balance Detail each worked AR out their own way, and disagreed:
// on Sigma the Detail totalled $337,532 against $183,274 on the books,
// because a payment posted before (or the same day as) the charge it covered
// had nothing to attach to and was dropped -- $154k of payments over 65
// tenants. Everything here now comes from buildArAging, which ties to the
// general ledger by construction: for every tenant, open charges minus the
// unapplied credit equals debits minus credits on their AR account(s).
//
// The rules (QuickBooks' AR Aging, as Sahil chose on 2026-10-06):
//   - Only posted entries dated on or before the report date.
//   - A charge is due on the day it is posted (rent is due on the 1st and is
//     charged on the 1st). Days past due are counted to the REPORT date, not
//     to today, so an "as of June 30" report ages as it stood on June 30.
//   - Aged by calendar month: Current = charged in the report's month, then
//     1, 2 and 3 months back, then 4+ (Sahil, 2026-10-06; it was by days).
//   - A payment settles what is open that day MONTH BY MONTH, oldest month
//     first: that month's late fees and other charges, then that month's
//     rent, then the next month (Sahil, 2026-10-07; paymentOrder.js is the
//     rule). A payment that arrives before its charge is carried as a
//     credit and applied to the next charge.
//   - Whatever credit is left over shows as a negative in Current, so the
//     report still adds up to the ledger.
//   - Tenants are told apart by tenant id (two tenants with one name stay
//     two rows; a renamed tenant stays one). Lines on the shared, bare
//     "Accounts Receivable" account fall back to the name in the entry's
//     description, as before.
import { safeNum, parseLocalDate } from "./helpers";
import { classifyCharge } from "./arrears";

export const AR_BUCKETS = ["current", "days30", "days60", "days90", "over90"];
// Tenant receivables age by CALENDAR MONTH, not by days (Sahil, 2026-10-06:
// rent is a monthly charge, and a days-based "Current" held only what was
// charged in the last few days). Current = charged in the report's month;
// then one, two and three months back; then four or more. The keys keep
// their old names so every consumer (reports, exports, collections) reads
// the same fields.
export const AR_BUCKET_LABELS = { current: "Current", days30: "1 month", days60: "2 months", days90: "3 months", over90: "4+ months" };

// The columns named by month for a given report date (Sahil, 2026-10-07):
// as of 2026-10-06 -> Oct 2026 · Sep · Aug · Jul · Jun & earlier.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function monthLabel(iso, { year = true } = {}) {
  const d = parseLocalDate(iso);
  if (isNaN(d)) return "";
  return MONTHS[d.getMonth()] + (year ? " " + d.getFullYear() : "");
}
export function arBucketLabelsFor(asOfDate) {
  const d = parseLocalDate(asOfDate);
  if (isNaN(d)) return { ...AR_BUCKET_LABELS };
  const back = (n) => { const x = new Date(d.getFullYear(), d.getMonth() - n, 1); return MONTHS[x.getMonth()] + (x.getFullYear() !== d.getFullYear() ? " " + x.getFullYear() : ""); };
  return { current: MONTHS[d.getMonth()] + " " + d.getFullYear(), days30: back(1), days60: back(2), days90: back(3), over90: back(4) + " & earlier" };
}

// The AR accounts: the bare legacy account, the per-tenant "AR - <name>"
// sub-accounts, and anything coded 1100*.
export function isArAccount(a) {
  return !!a && (a.name === "Accounts Receivable" || (a.name || "").startsWith("AR - ") || (a.code || "").startsWith("1100"));
}

// Whole months between the charge's month and the report's month.
export function monthsBetween(fromIso, toIso) {
  const f = parseLocalDate(fromIso), t = parseLocalDate(toIso);
  return (t.getFullYear() - f.getFullYear()) * 12 + (t.getMonth() - f.getMonth());
}
export function bucketForMonthsBack(months) {
  return months <= 0 ? "current" : months === 1 ? "days30" : months === 2 ? "days60" : months === 3 ? "days90" : "over90";
}
// Kept for callers that still think in days (the open-invoices colouring).
export function bucketForDaysPastDue(days) {
  return days <= 0 ? "current" : days <= 30 ? "days30" : days <= 60 ? "days60" : days <= 90 ? "days90" : "over90";
}

const tenantNameFromAccount = (a) => {
  const m = (a.name || "").match(/^AR - (.+?)(?:\s*\(|$)/);
  return m ? m[1].trim() : null;
};

// "Monthly rent — Tenant — property" style descriptions on the shared account.
const tenantFromDesc = (description, memo) => {
  const m = String(description || memo || "").match(/—\s*([A-Z][^—]*?)(?:\s*—|$)/);
  return m ? m[1].trim() : null;
};

// Who a line belongs to: { key, tenant, tenantId }.
export function arTenantOf(acct, je, line) {
  if (acct && acct.tenant_id !== null && acct.tenant_id !== undefined) {
    return { key: "t:" + acct.tenant_id, tenant: tenantNameFromAccount(acct) || acct.name, tenantId: acct.tenant_id };
  }
  const n = (acct && tenantNameFromAccount(acct)) || tenantFromDesc(je && je.description, line && line.memo) || "Unassigned";
  return { key: "n:" + n, tenant: n, tenantId: null };
}

const daysBetween = (fromIso, toIso) => Math.round((parseLocalDate(toIso) - parseLocalDate(fromIso)) / 86400000);

export function buildArAging(accounts, journalEntries, asOfDate) {
  const arAccounts = (accounts || []).filter(isArAccount);
  const arById = new Map(arAccounts.map(a => [a.id, a]));
  const arAccountIds = new Set(arById.keys());

  // Every AR line up to the report date, in posting order.
  const lines = [];
  for (const je of journalEntries || []) {
    if (je.status !== "posted" || !je.date || je.date > asOfDate) continue;
    for (const l of je.lines || []) {
      if (!arAccountIds.has(l.account_id)) continue;
      const debit = safeNum(l.debit), credit = safeNum(l.credit);
      if (debit <= 0 && credit <= 0) continue;
      lines.push({ je, l, debit, credit, who: arTenantOf(arById.get(l.account_id), je, l) });
    }
  }
  // Same day: charges (debits) before payments, so a payment made the day
  // the rent is charged covers that rent and the running balance never
  // dips negative in between (Sahil, 2026-10-07).
  lines.sort((x, y) => String(x.je.date).localeCompare(String(y.je.date))
    || ((x.debit > 0 ? 0 : 1) - (y.debit > 0 ? 0 : 1))
    || String(x.je.number || "").localeCompare(String(y.je.number || ""))
    || (safeNum(x.l.id) - safeNum(y.l.id)));

  // Per tenant: open charges, oldest first, and a carried credit.
  const tenants = new Map();
  for (const { je, l, debit, credit, who } of lines) {
    if (!tenants.has(who.key)) tenants.set(who.key, { key: who.key, tenant: who.tenant, tenantId: who.tenantId, charges: [], credit: 0, transactions: [] });
    const t = tenants.get(who.key);
    t.transactions.push({ date: je.date, jeId: je.id, number: je.number, description: je.description || "", memo: l.memo || "", debit, credit });
    if (debit > 0) {
      const c = { date: je.date, jeId: je.id, number: je.number, description: je.description || "", memo: l.memo || "", original: debit, paid: 0,
        category: classifyCharge({ reference: je.reference, memo: l.memo, description: je.description, transactionType: je.transaction_type }) };
      // A credit that arrived earlier covers this charge first.
      const applied = Math.min(t.credit, c.original);
      c.paid += applied; t.credit -= applied;
      t.charges.push(c);
    }
    if (credit > 0) {
      // Oldest month first; within a month, fees and other charges before
      // rent (Sahil, 2026-10-07; the same order as paymentOrder.js).
      let rem = credit;
      const monthOf = (d) => String(d || "").slice(0, 7);
      const order = t.charges.filter(c => c.original - c.paid > 0.005)
        .sort((a, b) => monthOf(a.date).localeCompare(monthOf(b.date)) || ((a.category === "rent" ? 1 : 0) - (b.category === "rent" ? 1 : 0)) || String(a.date).localeCompare(String(b.date)));
      for (const c of order) {
        if (rem <= 0.005) break;
        const applied = Math.min(rem, c.original - c.paid);
        c.paid += applied; rem -= applied;
      }
      if (rem > 0.005) t.credit += rem;
    }
  }

  const items = [], byTenant = [], totals = { current: 0, days30: 0, days60: 0, days90: 0, over90: 0, total: 0 };
  for (const t of tenants.values()) {
    const row = { tenant: t.tenant, tenant_id: t.tenantId, key: t.key, current: 0, days30: 0, days60: 0, days90: 0, over90: 0, total: 0, credit: 0 };
    for (const c of t.charges) {
      const due = Math.round((c.original - c.paid) * 100) / 100;
      if (due <= 0.005) continue;
      const daysPastDue = daysBetween(c.date, asOfDate);
      const monthsBack = monthsBetween(c.date, asOfDate);
      const bucket = bucketForMonthsBack(monthsBack);
      items.push({ key: t.key, tenant: t.tenant, tenantId: t.tenantId, date: c.date, dueDate: c.date, jeId: c.jeId, number: c.number,
        description: c.description, memo: c.memo, category: c.category, original: c.original, paid: Math.round(c.paid * 100) / 100, due, daysPastDue, monthsBack, bucket });
      row[bucket] += due; row.total += due;
    }
    if (t.credit > 0.005) {
      const credit = Math.round(t.credit * 100) / 100;
      row.credit = credit; row.current -= credit; row.total -= credit;
      items.push({ key: t.key, tenant: t.tenant, tenantId: t.tenantId, date: asOfDate, dueDate: asOfDate, jeId: null, number: "", description: "Unapplied credit",
        memo: "", original: 0, paid: 0, due: -credit, daysPastDue: 0, bucket: "current", unappliedCredit: true });
    }
    if (AR_BUCKETS.every(b => Math.abs(row[b]) < 0.005)) continue;
    for (const b of AR_BUCKETS) { row[b] = Math.round(row[b] * 100) / 100; totals[b] += row[b]; }
    row.total = Math.round(row.total * 100) / 100; totals.total += row.total;
    row.transactions = t.transactions;
    byTenant.push(row);
  }
  for (const b of [...AR_BUCKETS, "total"]) totals[b] = Math.round(totals[b] * 100) / 100;
  byTenant.sort((a, b) => b.total - a.total || String(a.tenant).localeCompare(String(b.tenant)));
  items.sort((a, b) => b.daysPastDue - a.daysPastDue || String(a.tenant).localeCompare(String(b.tenant)));

  // Every tenant with any AR activity, for the Customer Balance Detail
  // (which lists all transactions with a running balance, not only open ones).
  const allTenants = [...tenants.values()].map(t => {
    let bal = 0;
    const transactions = t.transactions.map(x => { bal += x.debit - x.credit; return { ...x, balance: Math.round(bal * 100) / 100 }; });
    return { key: t.key, tenant: t.tenant, tenant_id: t.tenantId, transactions, totalBalance: Math.round(bal * 100) / 100 };
  });

  return { items, byTenant, totals, allTenants, arAccountIds };
}
